/**
 * Every project has its task board and memory (Part 09 decision 3). Once
 * the Pinned page has landed without one of them, the owner window asks the
 * backend for it (`ensureTaskboardFrame` / `ensureMemoryFrame`, a
 * find-or-create under a row lock), once per project, frames generation and
 * tool; memory only with Pro access. A mirror promoted to owner asks then.
 * The frame lands in the Pinned row, not in the feed, so it never plays the
 * arrival animation.
 */
import { useEffect, useRef } from "react";
import { ensureMemoryFrame } from "@/desktop/onboarding/workspace-frames/ensure-memory-frame";
import { ensureTaskboardFrame } from "@/desktop/onboarding/workspace-frames/ensure-taskboard-frame";
import { useWindowRole } from "@/desktop/use-window-role";
import { useFramesStore } from "@/stores/frames-store";
import { selectWallContext } from "@/stores/wall-feed-store";

const ENSURE = {
  taskboard: ensureTaskboardFrame,
  memory: ensureMemoryFrame,
} as const;

type ProjectTool = keyof typeof ENSURE;

interface EnsureWallToolsOptions {
  projectId: string;
  /** The Pinned page has landed for this project and generation. */
  toolsLoaded: boolean;
  /** The plan includes the knowledge graph. */
  memory: boolean;
}

export function useEnsureWallTools({
  projectId,
  toolsLoaded,
  memory,
}: EnsureWallToolsOptions): void {
  const generation = useFramesStore((state) => state.generation);
  const owner = useWindowRole() === "owner";
  const asked = useRef(new Set<string>());

  useEffect(() => {
    if (!toolsLoaded || !owner) return;
    const { canonicalSingletons } = selectWallContext(
      useFramesStore.getState(),
    );
    const tools: ProjectTool[] = memory
      ? ["taskboard", "memory"]
      : ["taskboard"];
    const askedKeys = asked.current;
    const controller = new AbortController();
    const pending = new Set<string>();
    for (const tool of tools) {
      const key = `${projectId}:${generation}:${tool}`;
      if (canonicalSingletons.has(tool) || askedKeys.has(key)) continue;
      askedKeys.add(key);
      pending.add(key);
      ENSURE[tool](projectId, controller.signal)
        .catch(() => {})
        .finally(() => pending.delete(key));
    }
    // Another project or generation must not take this one's frames; an
    // ask cut short goes again on the next run, a failed one waits.
    return () => {
      controller.abort();
      for (const key of pending) askedKeys.delete(key);
    };
  }, [generation, memory, owner, projectId, toolsLoaded]);
}
